<template>
  <div>
    <div class="page-header"><div class="page-icon">📤</div><h2>销售记录</h2></div>

    <!-- 日期范围 + 搜索 -->
    <div class="filter-bar">
      <el-date-picker
        v-model="dateRange"
        type="daterange"
        range-separator="至"
        start-placeholder="开始日期"
        end-placeholder="结束日期"
        value-format="YYYY-MM-DD"
        :clearable="false"
        style="width: 360px;"
        @change="handleDateChange"
      ></el-date-picker>

      <el-input
        v-model="nameQuery"
        placeholder="搜索商品名称或备注关键字"
        prefix-icon="el-icon-search"
        clearable
        style="width: 300px; margin-left: 12px;"
      ></el-input>
    </div>

    <!-- 销售记录表格 -->
    <el-card class="box-card">
      <el-table :data="paginatedSales" stripe v-loading="loading">
        <!-- 出货日期列 -->
        <el-table-column label="出货日期">
          <template #default="{ row }">
            {{ formatDate(row.transaction_date) }}
          </template>
        </el-table-column>

        <!-- 商品详情列，显示每条总价、总数量和总金额 -->
        <el-table-column label="商品详情">
          <template #default="{ row }">
            <div>
              <div v-for="(item, index) in row.product_details" :key="index">
                {{ item }}
              </div>
              <div class="total-amount">
                本次销售总数量: {{ row.total_quantity }} 条, 总金额: {{ row.total_amount }} 元
              </div>
            </div>
          </template>
        </el-table-column>
        <!-- 备注列 -->
        <el-table-column label="备注">
          <template #default="{ row }">
            <div v-if="isEditing(row)">
              <el-input
                v-model="row.remark"
                placeholder="输入备注"
                clearable
                @blur="handleBlur(row)"
              ></el-input>
            </div>
            <div v-else @click="handleClick(row)">
              <span>{{ row.remark || '点击添加备注' }}</span>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="200">
          <template #default="{ row }">
            <el-button
              type="primary"
              size="small"
              @click="handlePrint(row)"
            >
              打印
            </el-button>
            <el-button
              type="danger"
              size="small"
              @click="confirmUndo(row)"
              :disabled="isUndoing[row.transaction_id]"
              style="margin-left: 6px;"
            >
              {{ isUndoing[row.transaction_id] ? "撤回中..." : "撤回" }}
            </el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <!-- 分页 -->
    <el-pagination
      v-if="totalItems > pageSize"
      :current-page="currentPage"
      :page-size="pageSize"
      :total="totalItems"
      layout="prev, pager, next, jumper, ->, total"
      @current-change="handlePageChange"
      style="margin-top: 20px; text-align: center;"
    ></el-pagination>
  </div>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import axios from "axios";
import API_BASE from '@/api';
import { ElMessage, ElMessageBox } from "element-plus";
import dayjs from "dayjs";
import { buildReceiptBytes } from '@/utils/escpos';
import { isWebUSPSupported, printToThermalPrinter } from '@/utils/thermalPrinter';
import { receiptSettings } from '@/utils/receiptSettings';

const sales = ref([]); // 当前日期范围内的销售记录
const nameQuery = ref(""); // 商品名称搜索框
const currentPage = ref(1); // 当前页码
const pageSize = ref(17); // 每页显示条数
const totalItems = ref(0); // 销售记录总数
const loading = ref(false);
const editingRow = ref(null); // 存储正在编辑的行
const isUndoing = ref({}); // 记录正在撤回的订单

// 日期范围（默认近 30 天）
const dateRange = ref([
  dayjs().subtract(29, 'day').format('YYYY-MM-DD'),
  dayjs().format('YYYY-MM-DD')
]);

// 处理日期变化
const handleDateChange = () => {
  currentPage.value = 1;
  getSales();
};

// 确认撤回操作
const confirmUndo = (row) => {
  ElMessageBox.confirm(
    '确定要撤回该笔交易吗？',
    '提示',
    {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning'
    }
  )
    .then(() => {
      handleUndo(row);
    })
    .catch(() => {
      // 用户取消操作，不做任何处理
    });
};

// 撤回交易
const handleUndo = async (row) => {
  try {
    const formattedDate = dayjs(row.transaction_date).format("YYYY-MM-DD HH:mm:ss");
    isUndoing.value[row.transaction_date] = true;

    const response = await axios.post(`${API_BASE}/api/transactions/undo`, {
      transaction_date: formattedDate
    });

    if (response.data.success) {
      ElMessage.success("整笔交易已撤回！");
      await getSales();
    } else {
      ElMessage.error("撤回失败：" + response.data.message);
    }
  } catch (error) {
    // 优先用后端返回的中文 message，否则才显示 axios 技术消息
    const backendMsg = error?.response?.data?.message;
    ElMessage.error(backendMsg ? `撤回失败：${backendMsg}` : `请求出错：${error.message}`);
  } finally {
    isUndoing.value[row.transaction_date] = false;
  }
};

// 判断是否是当前正在编辑的行
const isEditing = (row) => editingRow.value === row;

// 失去焦点时，保存备注并切换为文本模式
const handleBlur = async (row) => {
  editingRow.value = null;
  await updateRemark(row);
};

// 点击文本时，切换为编辑模式
const handleClick = (row) => {
  editingRow.value = row;
};

// 获取销售记录并计算总价、总数量和总金额
const getSales = async () => {
  loading.value = true;
  try {
    const [start, end] = dateRange.value;
    const response = await axios.get(`${API_BASE}/api/sales`, {
      params: { start, end }
    });
    if (response.data.success) {
      sales.value = response.data.sales.map((item) => {
        let totalAmount = 0; // 每笔记录的总金额
        let totalQuantity = 0; // 每笔记录的总数量
        const details = item.product_details.split("\n").map(detail => {
          const priceMatch = detail.match(/单价: (\d+\.\d{2}) 元/);
          const quantityMatch = detail.match(/数量: (\d+)/);
          if (priceMatch && quantityMatch) {
            const price = parseFloat(priceMatch[1]);
            const quantity = parseInt(quantityMatch[1], 10);
            const total = price * quantity; // 单条商品总价
            totalAmount += total; // 累加到总金额
            totalQuantity += quantity; // 累加到总数量
            return `${detail}, 金额: ${total.toFixed(1)} 元`;
          }
          return detail;
        });
        return {
          ...item,
          transaction_date: dayjs(item.transaction_date).isValid()
            ? dayjs(item.transaction_date).toISOString()
            : null,
          product_details: details,
          total_amount: totalAmount.toFixed(1), // 保存总金额
          total_quantity: totalQuantity // 保存总数量
        };
      });
      totalItems.value = response.data.sales.length;
    } else {
      ElMessage.error("获取销售记录失败：" + response.data.message);
    }
  } catch (error) {
    // 优先用后端返回的中文 message，否则才显示 axios 技术消息
    const backendMsg = error?.response?.data?.message;
    ElMessage.error(backendMsg ? `撤回失败：${backendMsg}` : `请求出错：${error.message}`);
  } finally {
    loading.value = false;
  }
};

// 更新备注
const updateRemark = async (row) => {
  try {
    const formattedDate = dayjs(row.transaction_date).format('YYYY-MM-DD HH:mm:ss');
    const response = await axios.post(`${API_BASE}/api/sales/update-remark`, {
      transaction_date: formattedDate,
      remark: row.remark,
    });

    if (response.data.success) {
      ElMessage.success("备注更新成功！");
    } else {
      ElMessage.error("备注更新失败：" + response.data.message);
    }
  } catch (error) {
    ElMessage.error("请求出错：" + error.message);
  }
};

// 格式化日期的函数
const formatDate = (date) => {
  if (!date) return "无效日期";
  return dayjs(date).format("YYYY-MM-DD HH:mm:ss");
};

// 打印小票（WebUSB ESC/POS 直连，字迹清晰）
async function handlePrint(row) {
  if (!isWebUSPSupported()) {
    ElMessage.error('当前浏览器不支持 WebUSB，请使用 Chrome 或 Edge 浏览器');
    return;
  }
  try {
    // 从商品详情文本解析出商品项
    const items = (row.product_details || []).map(d => {
      const nameMatch = d.match(/^(.+?), 单价:/);
      const qtyMatch = d.match(/数量: (\d+)/);
      const priceMatch = d.match(/单价: (\d+\.\d{1,2}) 元/);
      const subtotalMatch = d.match(/金额: (\d+\.\d{1,2}) 元/);
      return {
        name: nameMatch ? nameMatch[1].trim() : d,
        quantity: qtyMatch ? parseInt(qtyMatch[1], 10) : 0,
        price: priceMatch ? parseFloat(priceMatch[1]) : 0,
        subtotal: subtotalMatch ? parseFloat(subtotalMatch[1]) : 0
      };
    });

    const data = buildReceiptBytes({
      storeName: receiptSettings.getStoreName() || '店铺名',
      type: 'sale',
      typeLabel: receiptSettings.getSaleLabel(),
      date: formatDate(row.transaction_date),
      items,
      total: parseFloat(row.total_amount || 0),
      totalQuantity: row.total_quantity || 0,
      cols: receiptSettings.getColumnWidths(),
      footer: receiptSettings.getFooter()
    });

    console.log('[打印] 走 WebUSB ESC/POS 路径, 字节数:', data.length);
    const dev = await printToThermalPrinter(data);
    ElMessage.success('已发送到打印机: ' + (dev.productName || '热敏打印机'));
  } catch (e) {
    // 用户在弹框里取消选择不打扰
    if (e.name === 'NotFoundError') return;
    console.error('[打印] 失败:', e);
    ElMessage.error('打印失败：' + e.message);
  }
}

// 过滤销售记录（商品名称 + 备注）
const filteredSales = computed(() => {
  const q = nameQuery.value.trim().toLowerCase();
  if (!q) return sales.value;

  return sales.value.filter((sale) => {
    // 1) 商品详情命中
    const productHit = (sale.product_details || []).some((detail) =>
      String(detail).toLowerCase().includes(q)
    );
    if (productHit) return true;
    // 2) 备注命中（null/undefined/空串跳过）
    const remark = sale.remark;
    if (remark && String(remark).toLowerCase().includes(q)) return true;
    return false;
  });
});

// 搜索后重置到第 1 页
watch(filteredSales, () => {
  currentPage.value = 1;
});

// 分页后的销售记录
const paginatedSales = computed(() => {
  const startIndex = (currentPage.value - 1) * pageSize.value;
  const endIndex = currentPage.value * pageSize.value;
  return filteredSales.value.slice(startIndex, endIndex);
});

// 处理页码变化
const handlePageChange = (page) => {
  currentPage.value = page;
};

// 页面加载时获取数据
getSales();
</script>

<style scoped>
.filter-bar {
  display: flex;
  align-items: center;
  margin-bottom: 20px;
}

.box-card {
  margin-bottom: 16px;
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-card);
  border: 1px solid var(--border-lighter);
}

.total-amount {
  margin-top: 10px;
  font-weight: 600;
  color: var(--primary);
  font-size: 13px;
}
</style>